import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function About() {
  const stats = [
    { value: '10万+', label: '影视资源' },
    { value: '5000万+', label: '累计用户' },
    { value: '99.9%', label: '服务可用率' },
    { value: '68个', label: '全国节点' },
  ];

  const milestones = [
    { year: '2018', title: '平台创立', desc: '八戒电影网正式上线，致力于为用户提供高清流畅的在线观影体验。' },
    { year: '2019', title: '用户突破百万', desc: '凭借优质的服务和丰富的资源，平台注册用户突破100万。' },
    { year: '2020', title: '技术全面升级', desc: '引入CDN加速和AI调度技术，实现全国范围不缓冲流畅播放。' },
    { year: '2022', title: '4K高清上线', desc: '全面支持4K超高清画质，为用户带来影院级的视觉享受。' },
    { year: '2024', title: '用户突破5000万', desc: '平台累计服务用户超过5000万，成为行业领先的在线观影平台。' },
    { year: '2026', title: '智能推荐时代', desc: '引入AI智能推荐系统，精准匹配用户喜好，发现更多好片。' },
  ];

  const testimonials = [
    {
      name: '张先生',
      location: '北京',
      comment: '八戒电影网的播放速度真的很快，从来没有卡顿过，画质也很清晰，比其他平台好太多了。',
    },
    {
      name: '李女士',
      location: '上海',
      comment: '最吸引我的是完全无广告！打开就能看，不用忍受那些烦人的广告，观影体验非常好。',
    },
    {
      name: '王同学',
      location: '广州',
      comment: '资源真的很丰富，很多小众的文艺片都能找到。而且分类特别清晰，找片很方便。',
    },
    {
      name: '赵先生',
      location: '深圳',
      comment: '我是八戒电影网的老用户了，见证了平台的不断进步。现在4K画质真的很震撼！',
    },
    {
      name: '陈女士',
      location: '成都',
      comment: '操作界面简洁直观，家里的老人也能轻松使用。全家人周末一起看电影成了我们的固定节目。',
    },
    {
      name: '刘先生',
      location: '武汉',
      comment: '在其他平台经常遇到缓冲问题，换成八戒电影网后再也没有这种烦恼了，强烈推荐！',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于八戒电影网',
    description: '八戒电影网 - 不缓冲流畅播放无广告的高清电影在线观看平台',
    about: {
      '@type': 'Organization',
      name: '八戒电影网',
      description: '采用高速服务器确保不缓冲流畅播放，高清影视资源无广告快速加载',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '八戒电影网真的没有广告吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，八戒电影网从点击播放到进入正片，全程没有任何广告干扰，为您提供最纯净的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '播放会卡顿缓冲吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不会。八戒电影网采用先进的CDN加速技术和全国68个节点覆盖，确保在任何网络环境下都能实现不缓冲、流畅播放。',
        },
      },
      {
        '@type': 'Question',
        name: '支持什么画质？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '八戒电影网支持从720P到4K的多种画质选择，所有影片均经过专业转码处理，根据您的网络状况自动匹配最佳画质。',
        },
      },
      {
        '@type': 'Question',
        name: '有哪些类型的电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '平台拥有超过10万部影视资源，涵盖动作片、喜剧片、科幻片、爱情片、恐怖片、剧情片、动画片、纪录片、悬疑片、战争片等各种类型，满足不同用户的观影需求。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-3xl mx-auto text-center">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于八戒电影网
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                八戒电影网自2018年创立以来，始终秉持"不缓冲、流畅播放、无广告"的服务理念，致力于为广大电影爱好者提供最优质的高清在线观影体验。
              </p>
            </div>
          </div>
        </div>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-foreground-500 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 text-center mb-2">
                品牌介绍
              </h2>
              <p className="text-foreground-500 text-xs text-center mb-8">我们的故事与使命</p>

              <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                <p>
                  <strong className="text-foreground-900">八戒电影网</strong>是一个专注于高清电影在线观看的平台。我们深知，对于每一位电影爱好者来说，观影体验至关重要——<strong>不缓冲</strong>的流畅播放是基本要求，<strong>高清画质</strong>是观影的灵魂，<strong>无广告</strong>是纯粹的享受。
                </p>
                <p>
                  为此，我们投入大量资源建设了先进的技术基础设施。采用分布式CDN加速网络，在全国部署了68个服务节点，配合AI智能调度系统，确保每位用户都能享受到<strong>快速加载</strong>、<strong>流畅播放</strong>的观影体验。无论您使用的是什么网络环境，八戒电影网都能自动匹配最优线路，真正实现<strong>高清不卡</strong>。
                </p>
                <p>
                  我们的影视资源库涵盖了国内外最新大片、经典老片、热门剧集等超过10万部作品。从好莱坞商业巨制到欧洲艺术电影，从国产动画到日本动漫，您都能在<strong>八戒电影网</strong>找到心仪的影片。我们坚持<strong>免费高清</strong>的原则，让每一位用户都能无障碍地享受<strong>高清电影在线观看</strong>的乐趣。
                </p>
                <p>
                  在用户体验方面，<strong>八戒电影网</strong>始终坚持极简设计理念。我们移除了所有可能干扰观影体验的广告元素，从点击播放到进入正片，全程没有任何广告干扰。简洁直观的界面让您轻松找到想看的<strong>最新电影</strong>，智能推荐系统则帮助您发现更多好片。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 text-center mb-2">
                发展历程
              </h2>
              <p className="text-foreground-500 text-xs text-center mb-8">一路走来的重要里程碑</p>

              <div className="space-y-0">
                {milestones.map((m, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></div>
                      {index < milestones.length - 1 && (
                        <div className="w-0.5 flex-1 bg-background-200 my-1"></div>
                      )}
                    </div>
                    <div className={`pb-6 ${index === milestones.length - 1 ? 'pb-0' : ''}`}>
                      <span className="inline-block px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-semibold mb-1.5 whitespace-nowrap">
                        {m.year}
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{m.title}</h3>
                      <p className="text-foreground-500 text-xs">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 text-center mb-2">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-xs text-center mb-8">来自全国用户的真实评价</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {testimonials.map((t, index) => (
                  <div key={index} className="p-5 rounded-lg bg-background-50">
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-xs leading-relaxed mb-4">
                      "{t.comment}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-foreground-900 text-xs font-semibold">{t.name}</p>
                        <p className="text-foreground-400 text-xs">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 text-center mb-2">
                常见问题
              </h2>
              <p className="text-foreground-500 text-xs text-center mb-8">您可能关心的问题</p>

              <div className="space-y-3">
                {[
                  {
                    q: '八戒电影网真的没有广告吗？',
                    a: '是的，八戒电影网从点击播放到进入正片，全程没有任何广告干扰，为您提供最纯净的观影体验。这是我们对用户的承诺，也是我们的核心竞争力之一。',
                  },
                  {
                    q: '播放会卡顿缓冲吗？',
                    a: '不会。八戒电影网采用先进的CDN加速技术和全国68个节点覆盖，配合AI智能调度系统，确保在任何网络环境下都能实现不缓冲、流畅播放。我们承诺99.9%的服务可用率。',
                  },
                  {
                    q: '支持什么画质？',
                    a: '八戒电影网支持从720P到4K的多种画质选择。系统会根据您的网络状况自动匹配最佳画质，同时您也可以手动选择想要的清晰度。所有影片均经过专业转码处理。',
                  },
                  {
                    q: '有哪些类型的电影？',
                    a: '平台拥有超过10万部影视资源，涵盖动作片、喜剧片、科幻片、爱情片、恐怖片、剧情片、动画片、纪录片、悬疑片、战争片等十大类型，以及更多细分分类，满足不同用户的观影需求。',
                  },
                  {
                    q: '是否需要注册才能观看？',
                    a: '八戒电影网致力于提供最便捷的观影体验，大部分影片可以直接观看，无需注册。注册会员可以享受更多个性化功能，如收藏影片、观影历史记录等。',
                  },
                ].map((faq, index) => (
                  <details key={index} className="group rounded-lg bg-background-100 border border-background-200/70">
                    <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                      {faq.q}
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                    </summary>
                    <div className="px-4 pb-4 text-foreground-600 text-xs leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-foreground-500 text-xs mb-8">如有任何问题或建议，欢迎随时与我们联系</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-lg bg-background-50">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h3>
                  <p className="text-foreground-500 text-xs">service@bajiemovie.com</p>
                </div>
                <div className="p-5 rounded-lg bg-background-50">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-customer-service-line text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">在线客服</h3>
                  <p className="text-foreground-500 text-xs">7×24小时全天候服务</p>
                </div>
                <div className="p-5 rounded-lg bg-background-50">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-feedback-line text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">意见反馈</h3>
                  <p className="text-foreground-500 text-xs">欢迎提供宝贵意见</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}